import React, { useEffect, useState } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import { useForm, UseFormProps } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
  addDomainCustodian,
  deleteDomainCustodian,
  fetchOrgCustodians,
  loadDomainCustodians,
} from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_DOMAIN_CUSTODIANS_REQUEST,
  GET_ORG_CUSTODIANS_REQUEST,
} from 'js/constants/actionTypes';
import { TextField } from 'new/elements/TextField';
import { Button } from 'new/elements/Button';
import { isValidEmail } from 'js/lib/ValidationUtils';
import { showAlert } from 'new/stores/showAlert/action';
import { ReactComponent as PackageIcon } from 'new/components/icons/fi-rr-package.svg';
import Menu from 'js/components/elements/menu/Menu';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { DataCustodians } from 'new/components/settings/DataCustodians/DataCustodians';
import { useMarketplaceDomains } from 'new/hooks/adapters/useMarketplaceDomains';
import { useOrgCustodians } from 'new/hooks/adapters/useOrgCustodians';
import DomainCustodianListItem from './DomainCustodianListItem';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { Custodian } from 'new/entities/Custodian';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';

const useFormInitialProps = {
  mode: 'onChange',
  defaultValues: { email: '' },
} as UseFormProps<any>;

export const DataDomainOverviewTab = (props: any) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const domainId = props?.data?.id;
  const { id: orgId } = useCurrentOrg();
  const user = useCurrentUser();
  const isLoading = useIsLoading(GET_DOMAIN_CUSTODIANS_REQUEST, GET_ORG_CUSTODIANS_REQUEST);
  const readOnly = !props?.data?.hasCustodianAccess;
  const { custodiansByDomain } = useMarketplaceDomains();
  const currentOrgCustodians = useOrgCustodians();
  // User can delete custodians if they are an org custodian OR a custodian of this specific domain
  const canDeleteCustodians = user.isOrgCustodian || props?.data?.hasCustodianAccess;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm(useFormInitialProps);
  const [allCustodiansById, setAllCustodiansById] = useState([]);
  const [column1, setColumn1] = useState([]);
  const [column2, setColumn2] = useState([]);

  const onClickSettings = () => history.push(DataCustodians.screenConfig.path as any);

  const menuItems = [
    {
      label: 'Data Custodian Settings',
      icon: <PackageIcon />,
      onClick: onClickSettings,
    },
  ];

  useEffect(() => {
    dispatch(loadDomainCustodians(domainId));
    dispatch(fetchOrgCustodians(orgId));
  }, []);

  useEffect(() => {
    let newOrgCustodians = {};
    if (currentOrgCustodians) {
      newOrgCustodians = currentOrgCustodians;
    }
    let newDomainCustodians = {};
    if (custodiansByDomain && custodiansByDomain[domainId]) {
      newDomainCustodians = custodiansByDomain[domainId]?.custodiansById;
    }

    setAllCustodiansById([
      ...Object.values(newOrgCustodians),
      ...Object.values(newDomainCustodians),
    ]);
  }, [isLoading, custodiansByDomain, currentOrgCustodians]);

  useEffect(() => {
    const col1 = [];
    const col2 = [];
    allCustodiansById.forEach((custodian, index) => {
      if (index % 2 === 0) {
        col1.push(custodian);
      } else {
        col2.push(custodian);
      }
    });
    setColumn1(col1);
    setColumn2(col2);
  }, [allCustodiansById]);

  const onSubmit = (data) => {
    dispatch(addDomainCustodian(domainId, data?.email)).then(() => {
      dispatch(
        showAlert({
          title: `${data?.email} added successfully!`,
          severity: 'success',
        }),
      );
      reset();
    });
  };

  const onDeleteCustodian = (data: Custodian) => {
    dispatch(deleteDomainCustodian(domainId, data?.id));
  };

  const addDomainCustodianElement = (
    <Stack direction="row" gap="16px">
      <TextField
        placeholder="Add a Domain Custodian"
        className="input-custodian"
        type="email"
        error={!!errors?.email}
        {...register('email', {
          required: 'Please enter a valid email',
          validate: (email) => {
            return isValidEmail(email) || 'Email is not valid';
          },
        })}
      />
      <Button
        disabled={readOnly}
        variant="outlined"
        color="primary"
        onClick={handleSubmit(onSubmit)}
      >
        Add
      </Button>
    </Stack>
  );

  return (
    <div className="tab-content data-domain-overview-tab">
      <Stack direction="row" gap="40px" padding="16px" alignItems="center">
        <Stack direction="row" gap="8px" alignItems="center">
          <h2>Data Custodians</h2>
          {readOnly ? null : <Menu items={menuItems} horizontalDots />}
        </Stack>
        {readOnly ? null : addDomainCustodianElement}
      </Stack>
      <Box sx={{ flexGrow: 1 }}>
        {!isLoading && allCustodiansById ? (
          <Grid container spacing={16} gap="12px">
            <Grid item key="column1">
              {column1.map((custodian: Custodian, index) => (
                <DomainCustodianListItem
                  data={custodian}
                  onDeleteCustodian={onDeleteCustodian}
                  canDelete={canDeleteCustodians}
                  key={`column1-${index}`}
                />
              ))}
            </Grid>
            <Grid item key="column2">
              {column2.map((custodian: Custodian, index) => (
                <DomainCustodianListItem
                  data={custodian}
                  onDeleteCustodian={onDeleteCustodian}
                  canDelete={canDeleteCustodians}
                  key={`column2-${index}`}
                />
              ))}
            </Grid>
          </Grid>
        ) : null}
        {isLoading ? <CenteredLoader height={100} /> : null}
      </Box>
    </div>
  );
};
